/**
 * rother3 companion — the audit record shape, from either entry path.
 *
 * Two producers write into the companion's audit store and they do not agree:
 *  - a **share link** is `#d=<base64>` of `{ v, generatedAt, audit }`
 *    (`docs/data-schema.md` §4; encoded by `extension/src/utils/share-link.ts`)
 *  - an **imported export file** is the envelope `{ schemaVersion, type, data }`
 *    (`docs/data-schema.md` §2; parsed by `parseAuditFile()`)
 *
 * The third case is the **v2 export**, which has no `data` key at all and instead
 * groups fields into sub-objects — `business`, `reputation`, `reviews`, `hours`,
 * `qa`, `attributes`. Unwrapping therefore fell through to `return record`, every
 * flat lookup resolved `undefined`, and a real import rendered **"Unknown Business"**
 * with `[object Object]` beside it. The data was in the file the whole time, so the
 * failure read to the user as lost data rather than as a wrong key — the same
 * failure this module was written to prevent, one envelope generation later.
 * `flattenV2()` puts the flat names back without disturbing the nested ones.
 *
 * The workspace read `audit.data` unconditionally, so it rendered the import path
 * correctly and rendered a **blank** detail view for the share path: the record
 * arrived intact, every field resolved to `undefined`, and the fields loop skips
 * undefined, so the page came up empty with no error and no message anywhere. The
 * user had clicked "open in companion", the companion had opened, and it showed
 * them nothing — which reads as "my data is missing", not "we read the wrong key".
 *
 * Categories have the same split twice over. The extractor emits
 * `Array<{ name, isPrimary }>`, the share payload emits `string[]` (the popup maps
 * it, precisely because `compare.js` compares these field by field), and some
 * callers use `{ primary, secondary }`. The workspace read
 * `data.categories?.primary || data.categories`, which renders the object form as
 * `[object Object],[object Object]` — the same defect the popup already fixed once
 * in `toShareAudit`, left in place downstream.
 *
 * Its own module, not inlined, because `workspace.js` touches `document` at module
 * scope and therefore cannot be imported into a node test. This is the part worth
 * testing, so it has to be reachable.
 */

/**
 * Is this the v2 export envelope?
 *
 * v2 groups fields into sub-objects (`business`, `reputation`, `reviews`,
 * `hours`, `qa`, `attributes`) where v1 kept them flat. The `reviews` test is
 * the sharpest one: v1's `reviews` is an array, v2's is an object wrapping
 * `items`.
 */
function isV2Envelope(record) {
  if (record.business && typeof record.business === 'object') {return true;}
  if (record.reputation && typeof record.reputation === 'object') {return true;}
  if (record.reviews && typeof record.reviews === 'object' && !Array.isArray(record.reviews)) {return true;}
  return false;
}

/**
 * Flatten a v2 envelope onto the flat field names the rest of the companion reads.
 *
 * ## Why this exists
 *
 * Every companion view reads flat names (`fields.businessName`, `fields.rating`,
 * `fields.hours`). Those are correct for v1 and for share links, and wrong for
 * v2 exports, which nest the same values one level down. So a real v2 import
 * resolved every field to `undefined`: the recent list fell through to its
 * `'Unknown'` fallback and the workspace title rendered **"Unknown Business"**.
 *
 * That is the worst class of bug for this project — it looks like lost data, and
 * the data was in the file the whole time.
 *
 * Sub-objects are kept on the result. Anything that has learned to read v2
 * directly keeps working; this only adds the flat aliases.
 *
 * ## The one place flattening cannot be additive
 *
 * `reviews`, `hours`, `attributes` and `categories` each occupy a key that v1 used
 * for a *different* type: an array, a string, an array and an array respectively.
 * Putting the v1 reading in the flat name necessarily displaces the v2 wrapper —
 * `out.reviews = items` means `out.reviews.insights` no longer exists. The v1
 * reading wins, because that is the contract every existing view is written
 * against, and the displaced wrapper is preserved beside it under a predictable
 * `*Meta` key (`reviewsMeta`, `hoursMeta`, `attributesMeta`, `categoriesMeta`).
 * `business` and `reputation` do not collide and are passed through untouched.
 */
function flattenV2(record) {
  const out = { ...record };

  const take = (src, keys) => {
    if (!src || typeof src !== 'object') {return;}
    for (const key of keys) {
      if (src[key] !== undefined) {out[key] = src[key];}
    }
  };

  take(record.business, ['businessName', 'placeId', 'url', 'address', 'phone', 'website', 'extractedAt']);
  take(record.reputation, ['rating', 'reviewCount', 'distribution']);

  // v2: categories.current is the array; categoryNames() reads arrays directly.
  const cats = record.categories;
  if (cats && typeof cats === 'object' && Array.isArray(cats.current)) {
    out.categories = cats.current;
    out.categoriesMeta = cats;
  }

  // v1 exports hours as a string. v2 splits it into raw + parsed.
  if (record.hours && typeof record.hours === 'object') {
    if (typeof record.hours.raw === 'string') {out.hours = record.hours.raw;}
    else if (record.hours.parsed !== undefined) {out.hours = record.hours.parsed;}
    out.hoursMeta = record.hours;
  }

  if (record.reviews && typeof record.reviews === 'object' && Array.isArray(record.reviews.items)) {
    out.reviews = record.reviews.items;
    out.reviewsMeta = record.reviews;
  }
  if (record.qa && Array.isArray(record.qa.items)) {out.qaItems = record.qa.items;}
  if (record.attributes && Array.isArray(record.attributes.items)) {
    out.attributes = record.attributes.items;
    out.attributesMeta = record.attributes;
  }

  // v2 dropped the top-level `categorySource` and kept the boolean it derived.
  // Restore the name so completeness reporting reads the same on both versions.
  if (out.categorySource === undefined && cats && typeof cats === 'object') {
    if (cats.secondaryCategoriesPublished === true) {out.categorySource = 'page-state';}
    else if (cats.secondaryCategoriesPublished === false) {out.categorySource = 'dom-chip';}
  }

  return out;
}

/**
 * Unwrap either producer's envelope down to the audit's own fields.
 *
 * Tolerates a third shape — an already-unwrapped audit — because a stored record
 * may be handed over directly, and returning it unchanged is correct there.
 * Anything unusable becomes `{}` so callers get empty fields rather than a throw.
 */
export function auditFields(record) {
  if (!record || typeof record !== 'object') {return {};}

  let fields;
  // Share-link payload: { v, generatedAt, audit }
  if (record.audit && typeof record.audit === 'object') {
    fields = record.audit;
  // Export envelope v1: { schemaVersion, type, data }
  } else if (record.data && typeof record.data === 'object') {
    fields = record.data;
  } else {
    fields = record;
  }

  // Only v2 pays for a copy. Anything already flat is handed back by reference, which
  // callers and tests rely on: `auditFields(x) === x` for a v1 or shared record.
  return isV2Envelope(fields) ? flattenV2(fields) : fields;
}

/**
 * Category names as one display string, from any of the three shapes.
 *
 * Returns `''` when there is nothing to show, so the caller's
 * skip-if-empty field loop hides the row instead of printing "undefined".
 */
export function categoryNames(categories) {
  if (categories == null) {return '';}
  if (typeof categories === 'string') {return categories.trim();}

  if (Array.isArray(categories)) {
    return categories
      .map((c) => {
        if (typeof c === 'string') {return c.trim();}
        if (c && typeof c === 'object') {
          return typeof c.name === 'string' ? c.name.trim() : '';
        }
        return '';
      })
      .filter((n) => n.length > 0)
      .join(', ');
  }

  if (typeof categories === 'object') {
    const secondary = Array.isArray(categories.secondary) ? categories.secondary : [];
    return [categories.primary, ...secondary]
      .filter((c) => typeof c === 'string' && c.trim().length > 0)
      .map((c) => c.trim())
      .join(', ');
  }

  return String(categories);
}

/**
 * How complete the category list is, from the audit's own `categorySource`.
 *
 * ## Why this exists
 *
 * `categoryNames()` answers "what names are on this listing" and is correct either way.
 * What it cannot answer is "is this the whole list" — and after 2026-10-04 the answer
 * varies. A `page-state` read carries every category Google holds; a `dom-chip` read
 * carries the one chip Maps renders.
 *
 * The companion rendered both identically: one name and six names came out as the same
 * kind of row, and the single name read as a complete list. That is the failure this
 * project treats as worst — an absence read as an absence. The export already carries
 * `categorySource` precisely so a consumer can tell; the companion was not reading it.
 *
 * Returns `'complete'`, `'primary-only'` or `'unknown'`. **`unknown` is the honest
 * default**, not a fallback: an audit written before the field existed has not been shown
 * to be incomplete, and claiming either state would be asserting something unverified.
 */
export function categoryCompleteness(record) {
  const fields = auditFields(record);
  const source = fields.categorySource;
  if (source === 'page-state') {return 'complete';}
  if (source === 'dom-chip' || source === 'none') {return 'primary-only';}
  return 'unknown';
}

/**
 * The sentence to show beside a category list, or `''` when nothing needs saying.
 *
 * Only `primary-only` produces one. `complete` needs no caveat because the list *is*
 * the whole thing, and adding "all categories were read" under a full list is noise.
 */
export function categoryCompletenessNote(record) {
  const state = categoryCompleteness(record);
  if (state === 'primary-only') {
    return (
      'Primary category only. Maps does carry further categories for this listing, but ' +
      'the export that reached this page could not read them - open the listing with ' +
      'rother3 on Chrome, or Firefox 128+, to read the full list.'
    );
  }
  return '';
}

/**
 * What rother3 managed to read, and from where.
 *
 * ## Why this exists
 *
 * `reviewsMeta` is written by `flattenV2` and, until 2026-10-09, read by **nothing**. The
 * companion displayed one review number — `reviewCount`, the listing's own total — and the
 * count of reviews actually captured travelled in the file and was then discarded. So the
 * page showed "14,256" beside no indication of how many rother3 had read, which is the
 * failure mode this project treats as worst: the number a user checks is the one that is
 * not the one we measured.
 *
 * It is also the exact defect that was already fixed once, for categories. `categoryCompleteness`
 * exists for the same reason and the same shape, and the export already carries the field that
 * answers it. The reviews half was simply never built.
 *
 * ## Returns `null`, not a guess
 *
 * `null` means "this export says nothing about its own coverage" — a v1 export, a share link,
 * or a file written before the field existed. The caller renders nothing in that case, which
 * is the honest outcome: claiming `unknown` had captured 0 would report a number nobody
 * measured, and inventing a denominator would invent a percentage.
 */
export function reviewProvenance(record) {
  const fields = auditFields(record);
  const meta = fields.reviewsMeta;
  if (!meta || typeof meta !== 'object') {return null;}

  const coverage = meta.coverage;
  if (!coverage || typeof coverage !== 'object') {return null;}

  const captured = typeof coverage.captured === 'number' ? coverage.captured : null;
  if (captured === null) {return null;}

  const total = typeof coverage.totalReviews === 'number' ? coverage.totalReviews : null;

  /**
   * The share, recomputed from `captured / total` rather than read from
   * `sampledFraction`.
   *
   * Two older exports spell the denominator `ofTotal` instead of `totalReviews`, and a
   * fraction precomputed by a different version is one more thing that can disagree. The
   * two counts are the only inputs, and the file has to agree with itself.
   */
  const denominator = total ?? (typeof coverage.ofTotal === 'number' ? coverage.ofTotal : null);
  const sampledFraction =
    denominator !== null && denominator > 0 ? captured / denominator : null;

  return {
    captured,
    totalReviews: total,
    sampledFraction,
    /**
     * Which source produced the reviews. Defaults to `'dom'`, which is the only honest
     * value for a record written before this field existed: that data can only have come
     * from the rendered cards.
     */
    source: typeof meta.source === 'string' ? meta.source : 'dom',
    /**
     * `representative` is copied, never inferred. It is hard-coded `false` in the export
     * because Google chooses which reviews to publish, so the capture is *selected*, not
     * random — and that is a property of the data, not something a consumer may decide.
     */
    representative: coverage.representative === true,
  };
}

/**
 * The share, as it should be printed.
 *
 * `'<0.1%'` and never `'0.0%'`. 13 of 14,256 rounded to one decimal is `0.0%`, which reads
 * as "we got nothing" and would hide a working feature behind a formatting choice. Below
 * one tenth of a percent, the honest statement is that the figure is small, not that it is
 * zero.
 */
export function formatSampleShare(fraction) {
  if (fraction === null || fraction === undefined || !(fraction > 0)) {return null;}
  const percent = fraction * 100;
  if (percent < 0.1) {return '<0.1%';}
  if (percent < 10) {return `${percent.toFixed(1)}%`;}
  return `${Math.round(percent)}%`;
}

/**
 * A short label for where the reviews came from, in words rather than as a code.
 *
 * The export's own values (`payload`, `dom`, `payload+dom`, `none`) are an API. A user
 * looking at an audit file has no way to know what `payload+dom` means, and the distinction
 * matters: a read from Google's own data carries exact dates and translations that a read
 * off the rendered cards cannot.
 *
 * An unrecognised value returns `''` rather than being echoed, so a future source added by
 * a later version degrades to a missing label instead of printing a raw enum at the user.
 */
export function reviewSourceLabel(source) {
  switch (source) {
    case 'payload':
      return 'Read from the data Google loaded for this listing';
    case 'dom':
      return 'Read from the review cards shown on the page';
    case 'payload+dom':
      return 'Read from the data Google loaded, plus the review cards on the page';
    case 'none':
      return 'No reviews were read from this page';
    default:
      return '';
  }
}

/**
 * The "Reviews read" row's value, or `''` to hide the row.
 *
 * `''` for a v1 export is the important part: the fields loop skips empty values, so an
 * older file shows no row at all rather than "0 reviews read" — a number nobody measured,
 * and the most misleading thing this block could print.
 */
export function readSummaryText(record) {
  const info = reviewProvenance(record);
  if (info === null) {return '';}
  const share = formatSampleShare(info.sampledFraction);
  const total = info.totalReviews;
  return total === null
    ? `${info.captured} read`
    : `${info.captured} of ${total}${share ? ` (${share})` : ''}`;
}

/**
 * The sentence to show under the review figures, or `''` when nothing needs saying.
 *
 * ## Three refusals, all deliberate
 *
 * 1. **The caveat is unconditional.** It is returned whenever a capture was measured, not
 *    only when the share looks bad. A caveat behind a threshold is a threshold that can be
 *    tuned away by whoever raised it last.
 * 2. **The empty state is rother3's failure, not the listing's.** "No reviews were read"
 *    and "this business has no reviews" need opposite reactions from a reader, and only the
 *    first one is ever true here — a listing with no reviews reads as 0 captured for the
 *    same reason one with 14,000 does.
 * 3. **A `complete` state is not recognised.** There is no reading of these fields that
 *    means "all reviews", and inventing one would be a claim the export cannot support.
 */
export function reviewProvenanceNote(record) {
  const info = reviewProvenance(record);
  if (info === null) {return '';}

  const share = formatSampleShare(info.sampledFraction);
  const denominator = info.totalReviews;

  if (info.captured === 0) {
    return (
      'No reviews were read from this page. The listing may well have reviews - this is ' +
      'what rother3 managed to extract, not a statement about the business.'
    );
  }

  const count = `${info.captured} of ${denominator ?? '?'} reviews read`;
  const sharePart = share ? ` (${share})` : '';
  const source = reviewSourceLabel(info.source);

  const caveat =
    'A sample. Google chooses which reviews to publish, so these figures do not ' +
    'describe every review.';

  if (info.source === 'dom') {
    return (
      `${count}${sharePart}. ${caveat} Read from the review cards on the page, so ` +
      'dates are relative and there are no translations - open the listing in Chrome ' +
      'to read more.'
    );
  }
  return `${count}${sharePart}. ${caveat}${source ? ` ${source}.` : ''}`;
}

/**
 * The field-extra counts worth showing: how many of the captured reviews carry an exact
 * date, an owner reply, or a Google translation.
 *
 * Counted from `reviewsMeta.items` rather than trusted from the export, because the popup's
 * own `coverage` block does not carry these. Counting here also means a companion that is
 * handed a hand-edited file counts what is actually in it.
 *
 * Returns `''` for a v1 export, where the fields are absent by construction rather than
 * unread — an absence the user should not be told about as a failure.
 */
export function reviewExtrasSummary(record) {
  const fields = auditFields(record);
  const items = fields.reviewsMeta?.items;
  if (!Array.isArray(items) || items.length === 0) {return '';}

  const withAbsoluteDate = items.filter((r) => typeof r?.absoluteDate === 'string').length;
  const withOwnerReply = items.filter((r) => r?.ownerReply != null).length;
  const withTranslation = items.filter((r) => typeof r?.translatedText === 'string').length;

  const parts = [];
  if (withAbsoluteDate > 0) {parts.push(`${withAbsoluteDate} with an exact date`);}
  if (withOwnerReply > 0) {parts.push(`${withOwnerReply} with an owner reply`);}
  if (withTranslation > 0) {parts.push(`${withTranslation} translated by Google`);}
  return parts.join(' · ');
}

/**
 * The rating histogram, as five buckets with their shares.
 *
 * ## Why this is a census and not a sample
 *
 * `reputation.distribution` is Maps' own aggregate over **every** review the business has,
 * and its five buckets sum to the listing's review count. Nothing here is extrapolated
 * from the handful of reviews rother3 read, so the small-sample objection that removed the
 * Review Insights visualizer in 2026-09-30 does not reach it.
 *
 * That is also why it is worth charting at all: a rating of **4.6** with 10,644 five-star
 * reviews and 360 one-star reviews says the 4.6 is a ceiling effect, not a spread — and
 * that is not visible in the `Rating 4.6` row of the detail list.
 *
 * ## The share is the finding
 *
 * `10,644` beside `360` invites the reader to divide in the direction that flatters them.
 * 360 sounds substantial until you see it is 3.3%. The share is printed, and the bar is
 * scaled to the **largest bucket** rather than to the total, so the small buckets are
 * legible instead of being a row of slivers.
 *
 * Returns `null` when there is no distribution — a v1 export, or a listing where rother3
 * read none. The caller then renders nothing rather than an empty chart frame.
 */
export function ratingDistribution(record) {
  const fields = auditFields(record);
  const raw = fields.distribution;
  if (!Array.isArray(raw) || raw.length === 0) {return null;}

  const buckets = [5, 4, 3, 2, 1].map((star) => {
    const entry = raw.find((d) => Number(d?.star) === star);
    const count = typeof entry?.count === 'number' && Number.isFinite(entry.count) && entry.count >= 0
      ? entry.count
      : null;
    return { star, count };
  });

  if (buckets.every((b) => b.count === null)) {return null;}

  const total = buckets.reduce((sum, b) => sum + (b.count ?? 0), 0);
  const max = Math.max(...buckets.map((b) => b.count ?? 0));
  if (total === 0 || max === 0) {return null;}

  return {
    buckets: buckets.map((b) => ({
      ...b,
      /** Share of the total, 0-100. `null` for a bucket Maps did not report. */
      share: b.count === null ? null : (b.count / total) * 100,
      /**
       * Bar width as a percentage of the **largest** bucket, so a 3% row is still a
       * visible bar. Scaled to the total it would be a sliver, and the point of the
       * chart is precisely that the small buckets matter.
       */
      barPercent: b.count === null ? 0 : (b.count / max) * 100,
    })),
    total,
    /** A bucket Maps did not report, so the shares below do not cover the whole total. */
    partial: buckets.some((b) => b.count === null),
  };
}

/**
 * The busiest hour and day, from a crowd chart.
 *
 * Ties are kept whole. A previous implementation of this in the popup used `slice(0, 3)`
 * on the peak list, which invented an ordering: with four hours tied at the week's maximum
 * it kept three and dropped the fourth, so the summary line disagreed with the heatmap
 * printed directly beneath it.
 */
export function popularTimesSummary(record) {
  const fields = auditFields(record);
  const pt = fields.popularTimes;
  if (!pt || typeof pt !== 'object' || !Array.isArray(pt.days)) {return null;}

  const days = pt.days
    .filter((d) => d && typeof d.dayName === 'string' && Array.isArray(d.hours))
    .filter((d) => d.hours.some((h) => h && typeof h.hour === 'number' && typeof h.crowdLevel === 'number'));

  if (days.length === 0) {return null;}

  let peak = { level: -1, dayName: null, hour: null };
  for (const day of days) {
    for (const hour of day.hours) {
      if (hour.crowdLevel > peak.level) {
        peak = { level: hour.crowdLevel, dayName: day.dayName, hour: hour.hour };
      }
    }
  }
  if (peak.hour === null) {return null;}

  const busiest = Array.isArray(pt.busiestDays) ? pt.busiestDays.filter((d) => typeof d === 'string') : [];

  return {
    days,
    peakHour: peak.hour,
    peakLevel: peak.level,
    peakDay: peak.dayName,
    /** Every day tied for busiest, as Maps reported them. Never truncated to one. */
    busiestDays: busiest,
    /**
     * How many days the chart covered, against seven.
     *
     * A run that read 5 of 7 days must not render as a complete week. This project has
     * recorded that exact figure disagreeing with an independent DOM count, so the number
     * is stated rather than assumed.
     */
    daysRead: days.length,
    daysComplete: days.length === 7,
    hasLegendData: pt.peakHours != null,
  };
}
